import { useEffect, useRef } from "react";

import { useNavigate, useQueryParamsActions } from "../routing";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectActiveClassSession,
  selectCurrentPageId,
  selectCurrentSectionId,
  selectIsPresentationActive,
} from "store/selectors";
import { selectCurrentBreakoutGroupId, selectIsInBreakout } from "store/selectors/breakouts";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { viewStateActions } from "store/slices/view";
import { pageUrl } from "utils/urls";

interface UseBreakoutInSessionObserverProps {
  isInVirtualClassOrObserving: boolean;
}

export const useBreakoutInSessionObserver = ({
  isInVirtualClassOrObserving,
}: UseBreakoutInSessionObserverProps) => {
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const isPresentationActive = useAppSelector(selectIsPresentationActive);
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const classSession = useAppSelector(selectActiveClassSession);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const currentPageId = useAppSelector(selectCurrentPageId);
  const { canParticipateInSection } = useCapabilities();
  const { deleteQueryParams } = useQueryParamsActions();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const breakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const syncedPageId = classSession?.breakout_session_synced_page_id;

  const previousBreakoutSessionEndsAt = useRef<string | null | undefined>(undefined);
  const previousBreakoutGroupId = useRef<string | null | undefined>(undefined);
  const previousIsInVirtualClass = useRef(isInVirtualClassOrObserving);

  useEffect(() => {
    if (isInVirtualClassOrObserving && isInBreakout && !!breakoutGroupId) {
      deleteQueryParams([FOLLOWING_QUERY_PARAM]);
      dispatch(viewStateActions.setHasStudentLeftPresentation(false));
    }
  }, [
    isInBreakout,
    isPresentationActive,
    deleteQueryParams,
    dispatch,
    isInVirtualClassOrObserving,
    breakoutGroupId,
  ]);

  useEffect(() => {
    const breakoutSessionEndsAt = classSession?.breakout_session_ends_at;
    const canAutoNavigateToSyncedPage = canParticipateInSection || isObservingClass;

    if (
      !isInVirtualClassOrObserving ||
      !isInBreakout ||
      !canAutoNavigateToSyncedPage ||
      !sectionId ||
      !syncedPageId ||
      syncedPageId === currentPageId
    ) {
      previousBreakoutSessionEndsAt.current = breakoutSessionEndsAt;
      previousBreakoutGroupId.current = breakoutGroupId;
      previousIsInVirtualClass.current = isInVirtualClassOrObserving;
      return;
    }

    const breakoutJustStarted = !previousBreakoutSessionEndsAt.current && !!breakoutSessionEndsAt;
    const assignedToBreakoutGroup =
      !!breakoutGroupId && breakoutGroupId !== previousBreakoutGroupId.current;
    const joinedVirtualClassDuringBreakout =
      isInVirtualClassOrObserving && !previousIsInVirtualClass.current;

    if (breakoutJustStarted || assignedToBreakoutGroup || joinedVirtualClassDuringBreakout) {
      navigate(pageUrl(sectionId, syncedPageId));
    }

    previousBreakoutSessionEndsAt.current = breakoutSessionEndsAt;
    previousBreakoutGroupId.current = breakoutGroupId ?? null;
    previousIsInVirtualClass.current = isInVirtualClassOrObserving;
  }, [
    isInVirtualClassOrObserving,
    isInBreakout,
    canParticipateInSection,
    isObservingClass,
    sectionId,
    syncedPageId,
    currentPageId,
    classSession?.breakout_session_ends_at,
    breakoutGroupId,
    navigate,
  ]);
};
